import { redirect, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { useFetchRun } from "@/Hooks/useFetch";
export const BASE_URL = import.meta.env.VITE_API_URL;
import type { ModelLoginUserResponse } from "@models/ModelLoginUserResponse";
import type { BaseResponse } from "@models/base";
import NaverSignup from "./NaverSignup";
import { Loader2 } from "lucide-react";
import { setWithExpiry } from "@/utils/token";
// 버튼 컴포넌트
export const Button = ({
  children,
  variant = "primary",
  loading = false,
  onClick,
  type = "button",
}: {
  children: React.ReactNode;
  variant?: "primary" | "secondary";
  loading?: boolean;
  onClick?: () => void;
  type?: "button" | "submit";
}) => {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={loading}
      className={`
        w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200
        ${
          variant === "primary"
            ? "bg-[#03C75A] text-white hover:bg-[#02b350] hover:shadow-md"
            : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 hover:shadow-sm"
        }
        ${loading ? "opacity-50 cursor-not-allowed" : ""}
      `}
    >
      {loading ? (
        <div className="flex items-center justify-center">
          <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
          {children}
        </div>
      ) : (
        children
      )}
    </button>
  );
};

// 로그인 폼 컴포넌트
const LoginForm = ({
  onBack,
  ncode,
  onErrorChange,
}: {
  onBack: () => void;
  ncode: string | null;
  onErrorChange: (hasError: boolean) => void;
}) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    // 에러 상태가 변경될 때마다 부모 컴포넌트에 알림
    onErrorChange(!!errorMessage);
  }, [errorMessage, onErrorChange]);

  //로그인훅
  const loginHook = useFetchRun<BaseResponse<ModelLoginUserResponse>>({
    url: `${BASE_URL}/user/login`,
    method: "POST",
    body: { username: email, password: password },
  });

  //연동훅
  const linkHook = useFetchRun<BaseResponse<{}>>({
    url: `${BASE_URL}/user/naver/link`,
    method: "PUT",
    body: { ncode: ncode },
  });

  const loginProcess = async () => {
    if (!email || !password) {
      setErrorMessage("이메일과 비밀번호를 입력해주세요");
      return;
    }

    setLoading(true);
    setErrorMessage(null);

    try {
      const run = await loginHook.executeFetch();

      if (run.meta.code === 200) {
        setWithExpiry("token", run.data.token!);
        const linkRun = await linkHook.executeFetch();
        if (linkRun.meta.code === 200) {
          alert("연동이 완료되었습니다.");
          window.location.href = "/";
        } else {
          alert("연동에 실패했습니다. 잠시 후 다시 시도해주세요.");
        }
      } else {
        setErrorMessage(
          "로그인에 실패했습니다. 이메일과 비밀번호를 확인해주세요."
        );
      }
    } catch (error) {
      setErrorMessage(
        "서버 연결에 문제가 발생했습니다. 잠시 후 다시 시도해주세요."
      );
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="space-y-4 px-1">
      <div className="text-left">
        <label
          htmlFor="email"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          이메일
        </label>
        <input
          type="email"
          id="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#03C75A] focus:border-transparent outline-none transition-all duration-200"
          placeholder="가입하신 이메일을 입력하세요"
          required
        />
      </div>
      <div className="text-left">
        <label
          htmlFor="password"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          비밀번호
        </label>
        <input
          type="password"
          id="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#03C75A] focus:border-transparent outline-none transition-all duration-200"
          placeholder="비밀번호를 입력하세요"
          required
        />
      </div>

      {errorMessage && (
        <div className="mt-2 text-sm text-red-600 bg-red-50 p-3 rounded-md border border-red-200">
          {errorMessage}
        </div>
      )}

      <div className="pt-2">
        <Button
          type="submit"
          variant="primary"
          loading={loading}
          onClick={() => loginProcess()}
        >
          로그인하여 연동하기
        </Button>
        <div className="mt-2">
          <Button variant="secondary" onClick={onBack} loading={loading}>
            뒤로가기
          </Button>
        </div>
      </div>
    </form>
  );
};

// 메인 컴포넌트
export default () => {
  const [searchParams] = useSearchParams();
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [showSignupForm, setShowSignupForm] = useState(false);
  const [hasError, setHasError] = useState(false);
  const ncode = searchParams.get("ncode");

  if (!ncode) {
    redirect("/login");
  }

  // 컨테이너 높이 계산
  const getContainerHeight = () => {
    if (!showLoginForm && !showSignupForm) return "min-h-[120px]";
    if (showSignupForm && hasError) return "min-h-[640px]";
    if (showSignupForm) return "min-h-[580px]";
    if (showLoginForm) return "min-h-[300px]";
  };

  const handleBack = () => {
    setShowLoginForm(false);
    setShowSignupForm(false);
  };

  return (
    <div className="flex flex-col items-center justify-center p-4 my-12">
      <div
        className={`bg-white p-6 rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] w-full max-w-md transition-all duration-300 ease-in-out ${getContainerHeight()}`}
      >
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            네이버 계정 연동
          </h1>
          <p className="text-gray-600">
            네이버 계정과 연동된 아이디가 없습니다.
          </p>
        </div>

        <div
          className={`relative transition-all duration-500 ease-in-out ${getContainerHeight()}`}
        >
          <div
            className={`transform transition-all duration-200 ease-out absolute w-full
              ${
                showLoginForm || showSignupForm
                  ? "translate-y-8 opacity-0 pointer-events-none"
                  : "translate-y-0 opacity-100"
              }`}
            style={{
              transformOrigin: "center top",
              transitionProperty: "transform, opacity",
            }}
          >
            <div className="flex flex-col gap-3">
              <Button variant="primary" onClick={() => setShowLoginForm(true)}>
                기존 아이디 연동하기
              </Button>
              <Button
                variant="secondary"
                onClick={() => setShowSignupForm(true)}
              >
                신규 회원가입
              </Button>
            </div>
          </div>

          <div
            className={`transform transition-all duration-200 ease-out absolute w-full
              ${
                showLoginForm
                  ? "translate-y-0 opacity-100 scale-100"
                  : "-translate-y-4 opacity-0 scale-98 pointer-events-none"
              }`}
            style={{
              transformOrigin: "center top",
              transitionProperty: "transform, opacity",
            }}
          >
            <LoginForm
              onBack={handleBack}
              ncode={ncode}
              onErrorChange={setHasError}
            />
          </div>

          <div
            className={`transform transition-all duration-200 ease-out absolute w-full
              ${
                showSignupForm
                  ? "translate-y-0 opacity-100 scale-100"
                  : "-translate-y-4 opacity-0 scale-98 pointer-events-none"
              }`}
            style={{
              transformOrigin: "center top",
              transitionProperty: "transform, opacity",
            }}
          >
            <NaverSignup
              ncode={ncode}
              onBack={handleBack}
              onErrorChange={(hasError) => setHasError(hasError)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
